/**
 * API Slice
 * RTK Query API definitions with 401 handling
 */

import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query";
import type { LoginRequest, LoginResponse, SteConfigResponse } from "./types";
import { logout } from "../features/auth";

// Define the auth state shape for type safety
interface AuthState {
  token: string | null;
}

// Base URL for API with fallback and validation
const API_BASE_URL = (() => {
  const url = import.meta.env.VITE_API_BASE_URL;
  if (!url) {
    console.warn("VITE_API_BASE_URL is not set, using fallback");
    return "https://api.example.com";
  }
  return url;
})();

// Base query with auth headers
const baseQuery = fetchBaseQuery({
  baseUrl: API_BASE_URL,
  prepareHeaders: (headers, { getState }) => {
    // Get token from auth slice in Redux store
    const state = getState() as { auth: AuthState };
    const token = state.auth.token;

    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    headers.set("Content-Type", "application/json");
    return headers;
  },
});

// Custom base query that handles 401 responses
const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);

  // If we get a 401, clear auth and log out
  if (result.error && result.error.status === 401) {
    console.log("[API] 401 Unauthorized - logging out");
    api.dispatch(logout());
  }

  return result;
};

export const apiSlice = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["Agent", "Contacts"],
  endpoints: (builder) => ({
    // POST /login/ste
    // Login with STE token and receive agent context
    login: builder.mutation<LoginResponse, LoginRequest>({
      query: (credentials) => ({
        url: "/login/ste",
        method: "POST",
        body: credentials,
      }),
      invalidatesTags: ["Agent"],
    }),

    // GET /ste/config
    // Get STE configuration including contact list and customization
    getSteConfig: builder.query<SteConfigResponse, void>({
      query: () => "/ste/config",
      providesTags: ["Contacts"],
    }),

    // GET /ste/steUiAssets/{assetId}
    // Get URL for STE asset
    getSteAssetUrl: builder.query<{ url: string }, string>({
      query: (assetId) => `/ste/steUiAssets/${assetId}`,
    }),
  }),
});

// Export hooks for usage in components
export const {
  useLoginMutation,
  useGetSteConfigQuery,
  useGetSteAssetUrlQuery,
} = apiSlice;
